import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CustomerIcons from '@/icons/CustomerIcons'
import EyeIcons from '@/icons/EyeIcons'
import PersonRemoveIcons from '@/icons/PersonRemoveIcons'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import useDebounce from '@/hooks/useDebounce'
import ContactDetailModal from '../../../../../modals/ContactMarketingModals/Detail'
import {
    convertToHRContactSource,
    getMyLeads,
    markSpamContactSource,
} from '../../../services'
import { ContactSource } from '../../../types/response'
import { contactSourceColumns } from '../column'
import { useLeadFilters, encodeFilters } from '../LeadFiltersContext'
import {
    DataTableCustomized,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import type { SortOrder } from '@/components/ui/data-table-customized'
import Paper from '@/components/Paper'

const PAGE_SIZE = 10

const MyLeadsList: React.FC = () => {
    const { t } = useTranslation()
    const { t: tMar } = useTranslation('marketing')
    const [searchParams, setSearchParams] = useSearchParams()
    const { filters } = useLeadFilters()
    const { setConfirmation } = useConfirmation()

    const [selectedContactId, setSelectedContactId] = useState<number | null>(
        null
    )
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Search state with debounce
    const [searchInput, setSearchInput] = useState('')
    const debouncedSearch = useDebounce(searchInput, 300)

    // Pagination & sorting state
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZE)
    const [sortBy, setSortBy] = useState('contact_source_created_at')
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

    // Check URL params on mount to auto-open modal
    useEffect(() => {
        const contactIdFromUrl = searchParams.get('contact_source_id')
        if (contactIdFromUrl) {
            const contactId = Number(contactIdFromUrl)
            if (!isNaN(contactId)) {
                setSelectedContactId(contactId)
                setIsModalOpen(true)
            }
        }
    }, [searchParams])

    const { mutateAsync: convertToHRContactSourceMutation } =
        useMutationWithHandler(
            { mutationFn: convertToHRContactSource },
            { reactQueryKeys: QUERY_KEYS.MARKETING }
        )

    const { mutateAsync: markSpamContactSourceMutation } =
        useMutationWithHandler(
            { mutationFn: markSpamContactSource },
            { reactQueryKeys: QUERY_KEYS.MARKETING }
        )

    // Build API payload with encoded filters (base64)
    const queryPayload = useMemo(() => {
        const { ...encodableFilters } = filters
        return {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
            q: debouncedSearch || '',
            filters: encodeFilters(encodableFilters) || undefined,
        }
    }, [page, pageSize, sortBy, sortOrder, debouncedSearch, filters])

    const { data, isLoading, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MARKETING, 'my-leads-list', queryPayload],
        queryFn: () => getMyLeads(queryPayload),
    })

    const contacts = data?.results || []
    const total = data?.pagination?.total || 0

    // Reset page when filters change
    useEffect(() => {
        setPage(1)
    }, [filters])

    const handleOpenContact = useCallback(
        (contactSourceId: number) => {
            setSelectedContactId(contactSourceId)
            setIsModalOpen(true)
            const currentParams = Object.fromEntries(searchParams.entries())
            setSearchParams({
                ...currentParams,
                contact_source_id: contactSourceId.toString(),
            })
        },
        [searchParams, setSearchParams]
    )

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
        setSelectedContactId(null)
        const currentParams = Object.fromEntries(searchParams.entries())
        delete currentParams.contact_source_id
        setSearchParams(currentParams)
    }, [searchParams, setSearchParams])

    const handleConvertToHR = async (
        contactSourceId: number,
        contactSourceName: string
    ) => {
        setConfirmation({
            title: 'Convert To HR Contact',
            description: `${t('convert-description')} "${contactSourceId} - ${contactSourceName}" to Contact HR?`,
            onConfirm: async () => {
                try {
                    await convertToHRContactSourceMutation({
                        contact_source_id: contactSourceId,
                    })
                    toast.success(
                        `${t('convert-customer-to-hr-success')} ${contactSourceId}`
                    )
                } catch {
                    toast.error(
                        `${t('convert-customer-to-hr-error')} ${contactSourceId}`
                    )
                }
            },
            open: true,
        })
    }

    const handleMarkSpam = async (
        contactSourceId: number,
        contactSourceName: string
    ) => {
        setConfirmation({
            title: 'MarkSpam Contact',
            description: `${t('mark-spam')} "${contactSourceId} - ${contactSourceName}"?`,
            onConfirm: async () => {
                try {
                    await markSpamContactSourceMutation({
                        contact_source_id: contactSourceId,
                    })
                    toast.success(
                        `${t('convert-to-customer-success')} ${contactSourceId}`
                    )
                } catch {
                    toast.error(
                        `${t('convert-to-customer-error')} ${contactSourceId}`
                    )
                }
            },
            open: true,
        })
    }

    const columns = useMemo(
        () => contactSourceColumns(tMar, t, handleOpenContact),
        [tMar, t, handleOpenContact]
    )

    const actions: DataTableAction<ContactSource>[] = useMemo(
        () => [
            {
                key: 'view',
                label: t('view'),
                icon: <EyeIcons />,
                variant: 'info' as const,
                onClick: (row) => handleOpenContact(row?.contact_source_id),
            },
            {
                key: 'convert-hr',
                label: t('convert-to-hr'),
                icon: <CustomerIcons />,
                variant: 'warning' as const,
                onClick: (row) =>
                    handleConvertToHR(
                        row?.contact_source_id,
                        row?.contact_source_name
                    ),
                hidden: (row) => row.contact_source_status !== 'UNVERIFIED',
            },
            {
                key: 'mark-spam',
                label: t('markspam'),
                icon: <PersonRemoveIcons />,
                variant: 'danger' as const,
                onClick: (row) =>
                    handleMarkSpam(
                        row?.contact_source_id,
                        row?.contact_source_name
                    ),
                hidden: (row) => row.contact_source_status === 'MATCHED',
            },
        ],
        [t, handleOpenContact]
    )

    const getRowClassName = useCallback((row: ContactSource) => {
        if (['FOLLOWING'].includes(row?.contact_source_status ?? ''))
            return 'bg-info'
        if (
            ['UNVERIFIED', 'UN-MATCHED'].includes(
                row?.contact_source_status ?? ''
            )
        )
            return 'bg-warning'
        return ''
    }, [])

    return (
        <Paper aria-labelledby="ui:Marketing:MyLeadsList">
            <DataTableCustomized<ContactSource>
                columns={columns}
                rows={contacts}
                loading={isLoading}
                isFetching={isFetching}
                rowKey={(row) => String(row.contact_source_id)}
                onRowClick={(row) => handleOpenContact(row.contact_source_id)}
                rowClassName={getRowClassName}
                autoWidth
                actions={actions}
                toolbar={{
                    search: {
                        value: searchInput,
                        onChange: setSearchInput,
                        placeholder: t('contact-search-mkt'),
                    },
                }}
                sorting={{
                    sortBy,
                    sortOrder,
                    onSortChange: (newSortBy, newSortOrder) => {
                        setSortBy(newSortBy)
                        setSortOrder(newSortOrder)
                    },
                }}
                pagination={{
                    page,
                    pageSize,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                    pageSizeOptions: [10, 20, 50],
                }}
            />

            <ContactDetailModal
                open={isModalOpen}
                onClose={handleCloseModal}
                contactSourceID={selectedContactId}
            />
        </Paper>
    )
}

export default MyLeadsList
